// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Handgebaute SVG-Linie: Körpergewicht der letzten 90 Tage (kein Chart-Lib, wie VolumeChart)
import type { BodyWeight } from "@/lib/weight"
import { dayOf, daysAgo } from "@/lib/dates"
import { fmtWeight } from "@/lib/sets"

const W = 480
const H = 140
const PAD = { top: 10, right: 12, bottom: 18, left: 40 }
const DAYS = 90

/** "YYYY-MM-DD" → "07.08." (knapp, für die Achse) */
const short = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`

export default function WeightChart({ weights }: { weights: BodyWeight[] }) {
  const pts = weights
    .map((w) => ({ day: dayOf(w.date), kg: w.weight_kg }))
    .filter((p) => daysAgo(p.day) <= DAYS)
    .sort((a, b) => a.day.localeCompare(b.day))
  if (!pts.length) return null // leerer Zustand kommt von der Seite

  const lo = Math.min(...pts.map((p) => p.kg))
  const hi = Math.max(...pts.map((p) => p.kg))
  const span = Math.max(hi - lo, 1)
  const x = (day: string) => PAD.left + (1 - daysAgo(day) / DAYS) * (W - PAD.left - PAD.right)
  const y = (kg: number) => PAD.top + (1 - (kg - lo) / span) * (H - PAD.top - PAD.bottom)

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Körpergewicht, letzte ${DAYS} Tage: ${fmtWeight(pts[pts.length - 1].kg)} kg zuletzt`}
      style={{ display: "block", width: "100%", height: "auto" }}
    >
      {/* Achsenbeschriftung sparsam: nur Bereich (kg) und erster/letzter Tag */}
      <text x={PAD.left - 6} y={y(hi) + 3} textAnchor="end" fontSize={9} fill="var(--tanne)">{fmtWeight(hi)}</text>
      {hi !== lo && (
        <text x={PAD.left - 6} y={y(lo) + 3} textAnchor="end" fontSize={9} fill="var(--tanne)">{fmtWeight(lo)}</text>
      )}
      <text x={x(pts[0].day)} y={H - 4} textAnchor="middle" fontSize={9} fill="var(--tanne)">{short(pts[0].day)}</text>
      {pts.length > 1 && (
        <text x={x(pts[pts.length - 1].day)} y={H - 4} textAnchor="middle" fontSize={9} fill="var(--tanne)">
          {short(pts[pts.length - 1].day)}
        </text>
      )}
      <polyline
        points={pts.map((p) => `${x(p.day).toFixed(1)},${y(p.kg).toFixed(1)}`).join(" ")}
        fill="none"
        stroke="var(--moos)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {pts.map((p) => (
        <circle key={p.day} cx={x(p.day)} cy={y(p.kg)} r={3.5} fill="var(--moos)">
          <title>{`${short(p.day)} ${fmtWeight(p.kg)} kg`}</title>
        </circle>
      ))}
    </svg>
  )
}
